import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Search, CalendarIcon, Trash2, Send, Download, MessageSquare, Paperclip, ChevronRight, X } from "lucide-react";
import { useIsMobile } from "@/components/custom/AppShell";
import { AiSearchAssistant } from "@/components/custom/AiFeatures";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { listQuotes, deleteQuote, updateQuote, createQuote, getConfig, sendQuoteEmail, getQuotePdfUrl, listClients, createClient } from "@/lib/api";
import api from "@/lib/api";
import { parse } from "date-fns";
import { formatCurrency, flexMatch } from "@/lib/calculations";

const STATUS_COLORS = {
  "Not Started": "status-not-started",
  "Quote Booked": "status-quote-booked",
  "Awaiting Quote": "status-awaiting-quote",
  "Quote Sent": "status-quote-sent",
  "Job Booked": "status-job-booked",
  "Job Signed": "status-job-signed",
  "Invoiced": "status-invoiced",
  "Recall": "status-recall",
  "Recall Booked": "status-recall-booked",
  "Quote Accepted": "status-quote-accepted",
  "Cancelled": "status-cancelled",
  "Reschedule": "status-reschedule",
};

const MATERIALS_COLORS = {
  "Ordered": "bg-green-100 text-green-700",
  "Not Applicable": "bg-green-600 text-white",
  "Not Started": "bg-red-600 text-white",
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [quotes, setQuotes] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [sendQuoteId, setSendQuoteId] = useState(null);
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [showAll, setShowAll] = useState(false);
  const [showNewJob, setShowNewJob] = useState(false);
  const [recallId, setRecallId] = useState(null);
  const [recallReason, setRecallReason] = useState("");

  // Persist search & filter in URL
  const search = searchParams.get("q") || "";
  const statusFilter = searchParams.get("status") || null;
  const setSearch = (val) => {
    const params = new URLSearchParams(searchParams);
    if (val) params.set("q", val); else params.delete("q");
    setSearchParams(params, { replace: true });
  };
  const setStatusFilter = (val) => {
    const params = new URLSearchParams(searchParams);
    if (val) params.set("status", val); else params.delete("status");
    setSearchParams(params, { replace: true });
  };
  const [activityNotes, setActivityNotes] = useState([]);
  const [noteExpanded, setNoteExpanded] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [activityHeight, setActivityHeight] = useState(250);
  const activityDragRef = useRef(null);
  const [newJob, setNewJob] = useState({
    site_address: "", address_line_1: "", city: "", postcode: "",
    client_type: "Private", client_first_name: "", client_last_name: "",
    correspondence_email: "", correspondence_phone: "", tenant_phone: "", job_details: "", works_type: "",
  });
  const [postcodeSuggestions, setPostcodeSuggestions] = useState([]);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const addressSearchTimer = useRef(null);
  const [clientSearch, setClientSearch] = useState("");
  const [clientResults, setClientResults] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showCreateClient, setShowCreateClient] = useState(false);
  const [newClient, setNewClient] = useState({ first_name: "", last_name: "", client_type: "Private", email: "", phone: "" });
  const [woVisitDialog, setWoVisitDialog] = useState(null); // quote object for WO visit choice
  const [acceptDialog, setAcceptDialog] = useState(null); // quote object for accept choice when has_duplicates
  const [acceptDuplicates, setAcceptDuplicates] = useState([]);
  const [acceptLoading, setAcceptLoading] = useState(false);

  const PAGE_SIZE = 50;
  const isMobile = useIsMobile();

  // Client search for new job dialog
  useEffect(() => {
    if (clientSearch.length < 1) { setClientResults([]); return; }
    const t = setTimeout(() => {
      listClients(clientSearch).then(setClientResults).catch(() => {});
    }, 200);
    return () => clearTimeout(t);
  }, [clientSearch]);

  const selectClient = (c) => {
    setSelectedClient(c);
    setClientSearch("");
    setClientResults([]);
    setNewJob(p => ({
      ...p,
      client_first_name: c.first_name,
      client_last_name: c.last_name,
      client_type: c.client_type,
      correspondence_email: c.email || "",
      correspondence_phone: c.phone || "",
    }));
  };

  const handleCreateNewClient = async () => {
    if (!newClient.first_name.trim() || !newClient.last_name.trim()) { toast.error("Name required"); return; }
    try {
      const created = await createClient(newClient);
      toast.success("Client created");
      setShowCreateClient(false);
      selectClient(created);
      setNewClient({ first_name: "", last_name: "", client_type: "Private", email: "", phone: "" });
    } catch { toast.error("Failed to create client"); }
  };

  // Accept quote handler - opens dialog if has_duplicates
  const handleAcceptQuote = async (quote) => {
    if (quote.has_duplicates) {
      setAcceptDialog(quote);
      setAcceptLoading(true);
      try {
        const resp = await api.get(`/quotes/${quote.id}/duplicates`);
        setAcceptDuplicates(resp.data);
      } catch { toast.error("Failed to load quote options"); setAcceptDialog(null); } finally { setAcceptLoading(false); }
    } else {
      // No duplicates — accept directly
      if (window.confirm("Accept this quote? Status will change to Quote Accepted.")) {
        handleFieldUpdate(quote.id, "status", "Quote Accepted");
      }
    }
  };

  const confirmAcceptQuote = async (acceptedQuoteId) => {
    if (!acceptDialog) return;
    try {
      await api.post(`/quotes/${acceptDialog.id}/accept`, { accepted_quote_id: acceptedQuoteId });
      toast.success("Quote accepted");
      setAcceptDialog(null);
      setAcceptDuplicates([]);
      fetchQuotes();
    } catch { toast.error("Failed to accept quote"); }
  };

  // Address autocomplete using Nominatim (OpenStreetMap)
  const searchAddress = (query) => {
    if (addressSearchTimer.current) clearTimeout(addressSearchTimer.current);
    if (query.length < 3) { setAddressSuggestions([]); return; }
    addressSearchTimer.current = setTimeout(async () => {
      try {
        const resp = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&countrycodes=gb&format=json&addressdetails=1&limit=6`);
        const data = await resp.json();
        setAddressSuggestions(data.map(r => ({
          display: r.display_name,
          line1: [r.address?.house_number, r.address?.road].filter(Boolean).join(" ") || r.address?.road || r.display_name.split(",")[0],
          city: r.address?.city || r.address?.town || r.address?.suburb || r.address?.county || "",
          postcode: r.address?.postcode || "",
        })));
      } catch { setAddressSuggestions([]); }
    }, 300);
  };

  // Postcode lookup
  const lookupPostcode = async (pc) => {
    const clean = pc.replace(/\s/g, "").toUpperCase();
    if (clean.length < 5) { setPostcodeSuggestions([]); return; }
    try {
      const resp = await fetch(`https://api.postcodes.io/postcodes/${clean}`);
      const data = await resp.json();
      if (data.status === 200 && data.result) {
        const r = data.result;
        setNewJob(p => ({
          ...p,
          postcode: r.postcode,
          city: r.admin_district || r.admin_ward || "",
        }));
        setPostcodeSuggestions([]);
      } else {
        // Try autocomplete
        const resp2 = await fetch(`https://api.postcodes.io/postcodes/${clean}/autocomplete`);
        const data2 = await resp2.json();
        if (data2.result) setPostcodeSuggestions(data2.result.slice(0, 6));
        else setPostcodeSuggestions([]);
      }
    } catch { setPostcodeSuggestions([]); }
  };

  const handleSort = (col) => {
    if (sortCol === col) {
      if (sortDir === "asc") setSortDir("desc");
      else { setSortCol(null); setSortDir("asc"); }
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
  };

  useEffect(() => {
    getConfig().then(setConfig).catch(() => {});
  }, []);

  const fetchQuotes = useCallback(async () => {
    try {
      setLoading(true);
      const [data, notes] = await Promise.all([
        listQuotes(search),
        api.get("/activity-notes").then(r => r.data).catch(() => []),
      ]);
      setQuotes(data);
      setActivityNotes(notes);
    } catch {
      toast.error("Failed to load quotes");
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const timer = setTimeout(fetchQuotes, 300);
    return () => clearTimeout(timer);
  }, [fetchQuotes]);

  const handleFieldUpdate = async (quoteId, field, value) => {
    setQuotes(prev => prev.map(q => q.id === quoteId ? { ...q, [field]: value } : q));
    try {
      await updateQuote(quoteId, { [field]: value });
    } catch {
      toast.error("Failed to update");
      fetchQuotes();
    }
  };

  const handleNewJob = async () => {
    if (!newJob.client_first_name.trim()) { toast.error("Client first name is required"); return; }
    if (!newJob.client_last_name.trim()) { toast.error("Client last name is required"); return; }
    if (!newJob.address_line_1.trim()) { toast.error("Address Line 1 is required"); return; }
    if (!newJob.postcode.trim()) { toast.error("Postcode is required"); return; }
    const combinedAddress = [newJob.address_line_1.trim(), newJob.city.trim(), newJob.postcode.trim()].filter(Boolean).join(", ");
    if (!newJob.client_type) { toast.error("Client type is required"); return; }
    if (!newJob.correspondence_email.trim()) { toast.error("Correspondence email is required"); return; }
    if (!newJob.works_type) { toast.error("Works type is required"); return; }
    if (!newJob.job_details.trim()) { toast.error("Description is required"); return; }
    try {
      // Auto-create client if no existing client was selected
      if (!selectedClient) {
        await createClient({
          first_name: newJob.client_first_name.trim(),
          last_name: newJob.client_last_name.trim(),
          client_type: newJob.client_type,
          email: newJob.correspondence_email.trim(),
          phone: newJob.correspondence_phone.trim(),
          properties: combinedAddress ? [combinedAddress] : [],
        });
      }
      await createQuote({
        client_type: newJob.client_type,
        client_first_name: newJob.client_first_name.trim(),
        client_last_name: newJob.client_last_name.trim(),
        site_address: combinedAddress,
        correspondence_email: newJob.correspondence_email.trim(),
        correspondence_phone: newJob.correspondence_phone.trim(),
        tenant_phone: newJob.tenant_phone.trim(),
        job_details: newJob.job_details.trim(),
        works_type: newJob.works_type,
        labour_hours: 0,
        vat_enabled: true,
        status: "Not Started",
        start_date: "",
        materials_status: "Not Started",
        job_notes: "",
        quote_started: false,
        actions: [],
        measurements: [],
      });
      setShowNewJob(false);
      setNewJob({ site_address: "", address_line_1: "", city: "", postcode: "", client_type: "Private", client_first_name: "", client_last_name: "", correspondence_email: "", correspondence_phone: "", tenant_phone: "", job_details: "", works_type: "" });
      fetchQuotes();
      toast.success("Job created");
    } catch {
      toast.error("Failed to create job");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      // Soft delete — mark as deleted but keep quote/invoice data
      await updateQuote(deleteId, { is_deleted: true, deleted_at: new Date().toISOString() }, "System");
      toast.success("Job removed from dashboard. Quotes & invoices preserved.");
      setDeleteId(null);
      fetchQuotes();
    } catch (err) {
      toast.error("Failed to delete job");
    } 
  };

  const clientTypes = config?.client_types || ["Bairstow Eves", "Hamptons", "Private", "Gladstone", "Individual", "Keatons"];
  const statuses = config?.statuses || ["Not Started", "Quote Booked", "Quote Sent", "Quote Accepted", "Job Booked", "Overdue", "Reschedule", "Invoiced", "Recall"];
  const materialsStatuses = config?.materials_statuses || ["Not Started", "Ordered", "Not Applicable"];

  // Overdue is now a permanent status stored in the database
  const overdueCount = quotes.filter(q => q.status === "Overdue" || (q.status === "Quote Booked" && q.is_overdue)).length;

  const filteredQuotes = statusFilter === "Overdue"
    ? quotes.filter(q => q.status === "Overdue" || (q.status === "Quote Booked" && q.is_overdue))
    : statusFilter
      ? quotes.filter(q => (q.status || "Not Started") === statusFilter)
      : quotes;

  const sortedQuotes = [...filteredQuotes].sort((a, b) => {
    // When filtering by Quote Sent, sort by most recent sent first
    if (statusFilter === "Quote Sent" && !sortCol) {
      return new Date(b.updated_at || 0) - new Date(a.updated_at || 0);
    }
    if (!sortCol) return 0;
    let aVal, bVal;
    switch (sortCol) {
      case "address": aVal = a.site_address || ""; bVal = b.site_address || ""; break;
      case "client": aVal = a.client_type || ""; bVal = b.client_type || ""; break;
      case "status": aVal = statuses.indexOf(a.status || "Not Started"); bVal = statuses.indexOf(b.status || "Not Started"); return sortDir === "asc" ? aVal - bVal : bVal - aVal;
      case "date": aVal = a.start_date || ""; bVal = b.start_date || ""; break;
      case "materials": aVal = materialsStatuses.indexOf(a.materials_status || "Not Started"); bVal = materialsStatuses.indexOf(b.materials_status || "Not Started"); return sortDir === "asc" ? aVal - bVal : bVal - aVal;
      case "timeline": aVal = a.labour_hours || 0; bVal = b.labour_hours || 0; break;
      case "notes": aVal = a.job_notes || ""; bVal = b.job_notes || ""; break;
      case "quote": aVal = a.quote_started ? 1 : 0; bVal = b.quote_started ? 1 : 0; break;
      default: return 0;
    }
    // Blanks/zeros always sort last regardless of direction
    const aEmpty = aVal === "" || aVal === 0;
    const bEmpty = bVal === "" || bVal === 0;
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;
    if (bEmpty) return -1;
    if (typeof aVal === "number") return sortDir === "asc" ? aVal - bVal : bVal - aVal;
    const cmp = aVal.localeCompare(bVal, undefined, { sensitivity: "base" });
    return sortDir === "asc" ? cmp : -cmp;
  });

  const visibleQuotes = sortedQuotes.slice(0, PAGE_SIZE);
  const hasMore = sortedQuotes.length > PAGE_SIZE;

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      {/* Page Header */}
      <div className={isMobile ? "px-4 pb-3" : "px-8 pb-4"}>
        <div className={`flex items-center justify-between ${isMobile ? "mb-4" : "mb-6"}`}>
          <h1 className={isMobile ? "text-lg font-semibold" : "ui-page-title"}>Dashboard</h1>
          <div className="flex items-center gap-2">
            <AiSearchAssistant />
            {!isMobile && (
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/export/jobs`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors" style={{ background: "var(--bg)", color: "var(--text-secondary)", border: "1px solid var(--border)" }}>
                <Download className="w-3.5 h-3.5" /> Export CSV
              </a>
            )}
            <Button
              data-testid="create-quote-btn"
              onClick={() => navigate("/jobs/new")}
              className={`gap-2 font-semibold ${isMobile ? "h-9 px-3 text-sm" : ""}`}
              style={isMobile ? { background: "#E05C2A", color: "#fff" } : { background: "#E05C2A", color: "#fff", padding: "10px 20px", fontSize: "14px", fontWeight: 600, borderRadius: "12px", boxShadow: "0 2px 4px rgba(224, 92, 42, 0.3)" }}
            >
              <Plus className="w-4 h-4" /> New Job
            </Button>
          </div>
        </div>

        {/* Status counters */}
        <div className={`flex gap-2 ${isMobile ? "overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide" : "overflow-x-auto pb-2 scrollbar-hide mb-6"}`} data-testid="status-counters">
          {statuses.map(s => {
            const count = quotes.filter(q => (q.status || "Not Started") === s).length;
            const isActive = statusFilter === s;
            const colorMap = {
              "Not Started": "badge-danger",
              "Quote Booked": "badge-blue",
              "Awaiting Quote": "badge-warning",
              "Quote Sent": "badge-blue",
              "Quote Accepted": "badge-danger",
              "Job Booked": "badge-warning",
              "Job Signed": "badge-blue",
              "Invoiced": "badge-success",
              "Recall": "badge-warning",
              "Recall Booked": "badge-warning",
              "Cancelled": "badge-gray",
            };
            const needsAction = s === "Not Started" || s === "Quote Accepted" || s === "Overdue" || (s === "Quote Booked" && quotes.some(q => q.status === "Quote Booked" && q.is_overdue));
            return (
              <button
                key={s}
                onClick={() => setStatusFilter(isActive ? null : s)}
                className={`flex-shrink-0 rounded-xl text-center cursor-pointer transition-all ${isActive ? "ring-2 ring-offset-1 shadow-md" : "hover:shadow-md hover:-translate-y-0.5"}`}
                style={{
                  minWidth: isMobile ? "70px" : "100px",
                  minHeight: "72px",
                  padding: isMobile ? "8px 12px" : "12px 16px",
                  background: needsAction && count > 0 ? "#FEF2F2" : "var(--surface)",
                  border: isActive ? "1px solid var(--primary)" : "1px solid var(--border)",
                  borderTop: needsAction && count > 0 ? "3px solid #EF4444" : isActive ? "3px solid var(--primary)" : "1px solid var(--border)",
                }}
                data-testid={`status-count-${s.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {needsAction && count > 0 && <p className="text-[8px] font-bold uppercase tracking-wider mb-1" style={{ color: "#DC2626" }}>Action Required</p>}
                <p className="font-bold leading-tight" style={{ fontSize: isMobile ? "18px" : "28px", color: "var(--text-primary)" }}>{count}</p>
                <p className="font-semibold mt-1" style={{ fontSize: "11px", color: "var(--text-muted)" }}>{s}</p>
              </button>
            );
          })}
        </div>
        {isMobile && <div className="mb-3" />}

        {/* Recent Activity Feed - hidden on mobile */}
        {!isMobile && (() => {
          const now = new Date();
          const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
          const yesterdayStart = todayStart - 86400000;
          const cutoff = todayStart - 7 * 86400000;
          const recentActivities = [];
          quotes.forEach(q => {
            (q.activity_log || []).forEach(entry => {
              if (!entry.timestamp) return;
              const ts = new Date(entry.timestamp).getTime();
              if (ts >= cutoff) {
                recentActivities.push({ ...entry, address: q.site_address, quoteId: q.id });
              }
            });
          });
          recentActivities.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

          const formatTime = (ts) => {
            const d = new Date(ts);
            const isToday = d.getTime() >= todayStart;
            const isYesterday = d.getTime() >= yesterdayStart && d.getTime() < todayStart;
            const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
            if (isToday) return `Today ${time}`;
            if (isYesterday) return `Yesterday ${time}`;
            return `${d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" })} ${time}`;
          };

          return (
            <div className="ui-surface mb-4 relative" data-testid="activity-feed" style={{ overflow: "hidden" }}>
              <div className="p-4 pb-1">
                <h3 className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: "var(--text-secondary)" }}>
                  Recent Activity — Last 7 Days
                </h3>
                {recentActivities.length === 0 ? (
                  <div className="py-6 text-center text-sm" style={{ color: "var(--text-muted)" }}>No recent activity</div>
                ) : (
                <div className="space-y-2 overflow-y-auto pr-1" style={{ height: `${activityHeight - 70}px`, scrollbarWidth: "thin", scrollbarColor: "#D1D5DB transparent" }}>
                {recentActivities.slice(0, 20).map((a, i) => {
                  const key = `${a.quoteId}_${a.timestamp}`;
                  const notes = activityNotes.filter(n => n.quote_id === a.quoteId && n.activity_timestamp === a.timestamp);
                  const isExpanded = noteExpanded === key;
                  return (
                    <div key={key}>
                      <div className="flex items-start gap-3 text-sm">
                        <div className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ background: new Date(a.timestamp).getTime() >= todayStart ? "var(--primary)" : "var(--text-muted)" }} />
                        <div className="flex-1 min-w-0">
                          <p className="truncate" style={{ color: "var(--text-primary)" }}>
                            <span className="font-medium">{a.address?.split(",")[0] || "Job"}</span>
                            <span style={{ color: "var(--text-muted)" }}> — </span>
                            <span style={{ color: "var(--text-secondary)" }}>{a.action}</span>
                          </p>
                          {/* Existing notes */}
                          {notes.map(n => (
                            <div key={n.id} className="mt-1 p-2 rounded-lg text-xs" style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>
                              <p style={{ color: "var(--text-primary)" }}>{n.note}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="font-medium" style={{ color: "var(--primary)" }}>{n.user}</span>
                                <span style={{ color: "var(--text-muted)" }}>{formatTime(n.created_at)}</span>
                                {n.attachment_filename && (
                                  <button onClick={async () => {
                                    try {
                                      const resp = await api.get(`/activity-notes/${n.id}/file`, { responseType: "blob" });
                                      const url = URL.createObjectURL(resp.data);
                                      const a = document.createElement("a");
                                      a.href = url; a.download = n.attachment_filename; a.click();
                                      URL.revokeObjectURL(url);
                                    } catch { toast.error("Download failed"); }
                                  }} className="flex items-center gap-0.5 text-xs" style={{ color: "var(--primary)" }}>
                                    <Paperclip className="w-2.5 h-2.5" />{n.attachment_filename}
                                  </button>
                                )}
                                <button onClick={async () => {
                                  if (!window.confirm("Delete this note?")) return;
                                  await api.delete(`/activity-notes/${n.id}`);
                                  fetchQuotes();
                                }} className="ml-auto p-0.5 rounded hover:bg-red-50" style={{ color: "var(--text-muted)" }}>
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <span className="text-xs font-mono" style={{ color: "var(--text-muted)" }}>{formatTime(a.timestamp)}</span>
                          <button onClick={() => { setNoteExpanded(isExpanded ? null : key); setNoteText(""); }} className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-medium transition-colors" style={{ background: notes.length > 0 ? "var(--primary-light)" : "var(--bg)", color: notes.length > 0 ? "var(--primary)" : "var(--text-muted)", border: `1px solid ${notes.length > 0 ? "var(--primary)" : "var(--border)"}` }}>
                            <MessageSquare className="w-3 h-3" />
                            {notes.length > 0 ? `${notes.length} Note${notes.length > 1 ? "s" : ""}` : "Add Note"}
                          </button>
                        </div>
                      </div>
                      {/* Add note form */}
                      {isExpanded && (
                        <div className="ml-6 mt-2 mb-1 space-y-2">
                          <textarea value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Add a note..." rows={2} className="w-full text-xs rounded-lg p-2 resize-y" style={{ border: "1px solid var(--border)" }} />
                          <div className="flex gap-2">
                            <Button size="sm" className="btn-primary text-xs h-7 px-3" onClick={async () => {
                              if (!noteText.trim()) { toast.error("Note is empty"); return; }
                              await api.post("/activity-notes", { quote_id: a.quoteId, activity_timestamp: a.timestamp, note: noteText.trim(), user: localStorage.getItem("desktop_user_name") || "" });
                              toast.success("Note added");
                              setNoteExpanded(null); setNoteText("");
                              fetchQuotes();
                            }}>Add Note</Button>
                            <label className="inline-flex items-center gap-1 px-3 h-7 rounded text-xs font-medium cursor-pointer" style={{ background: "var(--bg)", border: "1px solid var(--border)", color: "var(--text-secondary)" }}>
                              <Paperclip className="w-3 h-3" /> Attach
                              <input type="file" className="hidden" onChange={async (e) => {
                                const f = e.target.files?.[0]; if (!f) return;
                                const noteResp = await api.post("/activity-notes", { quote_id: a.quoteId, activity_timestamp: a.timestamp, note: noteText.trim() || f.name, user: localStorage.getItem("desktop_user_name") || "" });
                                const fd = new FormData(); fd.append("file", f);
                                await api.post(`/activity-notes/${noteResp.data.id}/attachment`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                                toast.success("Note with attachment added");
                                setNoteExpanded(null); setNoteText("");
                                fetchQuotes();
                                e.target.value = "";
                              }} />
                            </label>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              )}
              </div>
              {/* Bottom drag handle */}
              <div
                className="h-3 cursor-ns-resize flex items-center justify-center hover:bg-slate-100 transition-colors"
                onMouseDown={(e) => {
                  e.preventDefault();
                  const startY = e.clientY;
                  const startH = activityHeight;
                  const onMove = (ev) => { setActivityHeight(Math.max(250, Math.min(600, startH + (ev.clientY - startY)))); };
                  const onUp = () => { document.removeEventListener("mousemove", onMove); document.removeEventListener("mouseup", onUp); };
                  document.addEventListener("mousemove", onMove);
                  document.addEventListener("mouseup", onUp);
                }}
                onTouchStart={(e) => {
                  const startY = e.touches[0].clientY;
                  const startH = activityHeight;
                  const onMove = (ev) => { setActivityHeight(Math.max(250, Math.min(600, startH + (ev.touches[0].clientY - startY)))); };
                  const onEnd = () => { document.removeEventListener("touchmove", onMove); document.removeEventListener("touchend", onEnd); };
                  document.addEventListener("touchmove", onMove);
                  document.addEventListener("touchend", onEnd);
                }}
              >
                <div className="w-10 h-1 rounded-full bg-slate-300" />
              </div>
            </div>
          );
        })()}

        {/* Search */}
        <div className={`flex ${isMobile ? "flex-col gap-2" : "items-center gap-4"} mb-4`}>
          <div className={`relative ${isMobile ? "w-full" : "flex-1 max-w-sm"}`}>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "var(--text-muted)" }} />
            <Input
              data-testid="search-input"
              placeholder="Search by address or client..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 border-slate-300 focus:border-slate-900 rounded-sm h-9 text-sm"
            />
          </div>
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-400">
              {statusFilter ? `${filteredQuotes.length} of ${quotes.length} jobs` : `${quotes.length} job${quotes.length !== 1 ? "s" : ""}`}
              {statusFilter && (
                <button onClick={() => setStatusFilter(null)} className="ml-2 text-orange-500 hover:text-orange-600 font-semibold">Clear filter</button>
              )}
            </p>
            {hasMore && (
              <button
                onClick={() => setShowAll(true)}
                className="text-xs font-semibold text-orange-500 hover:text-orange-600 transition-colors ml-3"
                data-testid="see-all-btn"
              >
                See All
              </button>
            )}
          </div>
        </div>

        {/* Job list - Cards on mobile, Table on desktop */}
        {isMobile ? (
          <div className="space-y-2" data-testid="job-tracker-cards">
            {loading ? (
              <div className="text-center py-10 text-slate-400">Loading...</div>
            ) : quotes.length === 0 ? (
              <div className="text-center py-10 text-slate-400">No jobs yet. Tap "New Job" to get started.</div>
            ) : (
              sortedQuotes.slice(0, PAGE_SIZE).map((q, i) => (
                <MobileJobCard
                  key={q.id}
                  quote={q}
                  index={i}
                  onNavigate={() => navigate(`/quotes/${q.accepted_quote_id || q.id}`)}
                  onBook={() => navigate(`/calendar?book=${q.id}`)}
                  onCreateQuote={() => navigate(`/quotes/${q.id}/edit`)}
                  statusFilter={statusFilter}
                />
              ))
            )}
            {hasMore && (
              <div className="text-center py-3 text-xs" style={{ color: "var(--text-muted)" }}>
                Showing {PAGE_SIZE} of {quotes.length} jobs
              </div>
            )}
          </div>
        ) : (
        <>
        {/* Spreadsheet table */}
        <div className="ui-surface overflow-x-auto" data-testid="job-tracker-table">
          <table className="ui-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <SortHeader col="address" label="Address" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} className="min-w-[200px]" />
                <SortHeader col="status" label="Status" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} className="min-w-[150px]" />
                <SortHeader col="date" label="Start Date" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} className="min-w-[130px]" />
                <th className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500 w-[70px]"></th>
                <SortHeader col="materials" label="Materials" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} className="min-w-[140px]" />
                <SortHeader col="timeline" label="Timeline" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} className="min-w-[70px]" />
                <SortHeader col="notes" label="Notes" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} className="min-w-[200px]" />
                <SortHeader col="quote" label="Quote" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} className="min-w-[160px]" />
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-10 text-slate-400">Loading...</td></tr>
              ) : quotes.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-10 text-slate-400">No jobs yet. Click "New Job" to get started.</td></tr>
              ) : (
                sortedQuotes.slice(0, PAGE_SIZE).map((q, i) => (
                  <JobRow
                    key={q.id}
                    quote={q}
                    index={i}
                    clientTypes={clientTypes}
                    statuses={statuses}
                    materialsStatuses={materialsStatuses}
                    onFieldUpdate={handleFieldUpdate}
                    onNavigate={() => navigate(`/quotes/${q.accepted_quote_id || q.id}`)}
                    onCreateQuote={() => {
                      navigate(`/quotes/${q.id}/edit`);
                    }}
                    onDelete={() => setDeleteId(q.id)}
                    onSendQuote={() => setSendQuoteId(q.id)}
                    onBook={() => navigate(`/calendar?book=${q.id}`)}
                    onView={() => navigate(`/quotes/${q.accepted_quote_id || q.id}`)}
                    onInvoice={(qid) => navigate(`/quotes/${qid}?invoice=true`)}
                    onRecall={() => { setRecallId(q.id); setRecallReason(""); }}
                    onAccept={() => handleAcceptQuote(q)}
                    statusFilter={statusFilter}
                    onWoVisit={(q) => setWoVisitDialog(q)}
                    isOverdue={q.status === "Overdue" || (q.status === "Quote Booked" && q.is_overdue)}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
        {hasMore && (
          <div className="text-center py-3 text-xs" style={{ color: "var(--text-muted)" }}>
            Showing {PAGE_SIZE} of {quotes.length} jobs
          </div>
        )}
        </>
        )}
      </div>

      {/* New Job Dialog */}
      <Dialog open={showNewJob} onOpenChange={setShowNewJob}>
        <DialogContent data-testid="new-job-dialog">
          <DialogHeader>
            <DialogTitle>New Job</DialogTitle>
            <DialogDescription>Enter the job details to create a new entry.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {/* Client Search */}
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Client *</Label>
              {selectedClient ? (
                <div className="flex items-center gap-2 mt-1 p-2 bg-slate-50 border border-slate-200 rounded-sm">
                  <div className="w-7 h-7 rounded-full bg-slate-900 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                    {selectedClient.first_name?.[0]}{selectedClient.last_name?.[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{selectedClient.first_name} {selectedClient.last_name}</p>
                    <p className="text-[10px] text-slate-400">{selectedClient.client_type} {selectedClient.email ? `— ${selectedClient.email}` : ""}</p>
                  </div>
                  <button onClick={() => { setSelectedClient(null); setNewJob(p => ({ ...p, client_first_name: "", client_last_name: "", client_type: "Private", correspondence_email: "", correspondence_phone: "" })); }} className="text-xs text-slate-400 hover:text-red-500">Change</button>
                </div>
              ) : (
                <div className="relative mt-1">
                  <Input
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    placeholder="Search existing clients..."
                    className="rounded-sm"
                    data-testid="client-search-input"
                  />
                  {clientResults.length > 0 && (
                    <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-lg max-h-48 overflow-y-auto">
                      {clientResults.map(c => (
                        <button key={c.id} onClick={() => selectClient(c)} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 text-left border-b border-slate-100 last:border-0">
                          <div className="w-6 h-6 rounded-full bg-slate-800 text-white flex items-center justify-center text-[9px] font-bold flex-shrink-0">
                            {c.first_name?.[0]}{c.last_name?.[0]}
                          </div>
                          <div>
                            <p className="text-sm font-medium">{c.first_name} {c.last_name}</p>
                            <p className="text-[10px] text-slate-400">{c.client_type}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Name fields (auto-filled from client or manual) */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Client First Name *</Label>
                <Input
                  value={newJob.client_first_name}
                  onChange={(e) => setNewJob(p => ({ ...p, client_first_name: e.target.value }))}
                  placeholder="First name"
                  className="rounded-sm mt-1"
                  data-testid="new-job-first-name"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Client Last Name *</Label>
                <Input
                  value={newJob.client_last_name}
                  onChange={(e) => setNewJob(p => ({ ...p, client_last_name: e.target.value }))}
                  placeholder="Last name"
                  className="rounded-sm mt-1"
                  data-testid="new-job-last-name"
                />
              </div>
            </div>
            <div className="relative">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Address Line 1 *</Label>
              <Input
                value={newJob.address_line_1}
                onChange={(e) => {
                  setNewJob(p => ({ ...p, address_line_1: e.target.value }));
                  searchAddress(e.target.value);
                }}
                placeholder="Start typing address..."
                className="rounded-sm mt-1"
                data-testid="new-job-address"
              />
              {addressSuggestions.length > 0 && (
                <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-52 overflow-y-auto" style={{ borderColor: "var(--border)" }}>
                  {addressSuggestions.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        setNewJob(p => ({ ...p, address_line_1: s.line1, city: s.city, postcode: s.postcode }));
                        setAddressSuggestions([]);
                      }}
                      className="w-full text-left px-4 py-3 text-sm hover:bg-slate-50 border-b last:border-0 transition-colors"
                      style={{ borderColor: "var(--border)" }}
                    >
                      <span className="font-medium" style={{ color: "var(--text-primary)" }}>{s.line1}</span>
                      {s.city && <span style={{ color: "var(--text-secondary)" }}>, {s.city}</span>}
                      {s.postcode && <span style={{ color: "var(--text-muted)" }}> {s.postcode}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">City</Label>
                <Input
                  value={newJob.city}
                  onChange={(e) => setNewJob(p => ({ ...p, city: e.target.value }))}
                  placeholder="e.g. London"
                  className="rounded-sm mt-1"
                  data-testid="new-job-city"
                />
              </div>
              <div className="relative">
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Postcode *</Label>
                <Input
                  value={newJob.postcode}
                  onChange={(e) => {
                    const val = e.target.value;
                    setNewJob(p => ({ ...p, postcode: val }));
                    lookupPostcode(val);
                  }}
                  placeholder="e.g. E15 3AB"
                  className="rounded-sm mt-1"
                  data-testid="new-job-postcode"
                />
                {postcodeSuggestions.length > 0 && (
                  <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border rounded-sm shadow-lg max-h-36 overflow-y-auto" style={{ borderColor: "var(--border)" }}>
                    {postcodeSuggestions.map(pc => (
                      <button
                        key={pc}
                        onClick={() => {
                          setNewJob(p => ({ ...p, postcode: pc }));
                          setPostcodeSuggestions([]);
                          lookupPostcode(pc);
                        }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b last:border-0"
                        style={{ borderColor: "var(--border)" }}
                      >
                        {pc}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Client Type *</Label>
              <Select value={newJob.client_type} onValueChange={(v) => setNewJob(p => ({ ...p, client_type: v }))}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="new-job-client-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {clientTypes.map(ct => <SelectItem key={ct} value={ct}>{ct}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Works Type *</Label>
              <Select value={newJob.works_type || ""} onValueChange={(v) => setNewJob(p => ({ ...p, works_type: v }))}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="new-job-works-type">
                  <SelectValue placeholder="Select works type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Quote Request">Quote Request</SelectItem>
                  <SelectItem value="Call Out">Call Out</SelectItem>
                  <SelectItem value="Works Order">Works Order</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Correspondence Email *</Label>
                <Input
                  type="email"
                  value={newJob.correspondence_email}
                  onChange={(e) => setNewJob(p => ({ ...p, correspondence_email: e.target.value }))}
                  placeholder="email@example.com"
                  className="rounded-sm mt-1"
                  data-testid="new-job-email"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Correspondence Phone</Label>
                <Input
                  value={newJob.correspondence_phone}
                  onChange={(e) => setNewJob(p => ({ ...p, correspondence_phone: e.target.value }))}
                  placeholder="07xxx xxx xxx"
                  className="rounded-sm mt-1"
                  data-testid="new-job-phone"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Tenant Phone <span className="text-slate-400 normal-case">(if applicable)</span></Label>
              <Input
                value={newJob.tenant_phone}
                onChange={(e) => setNewJob(p => ({ ...p, tenant_phone: e.target.value }))}
                placeholder="07xxx xxx xxx"
                className="rounded-sm mt-1"
                data-testid="new-job-tenant-phone"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Description *</Label>
              <textarea
                value={newJob.job_details}
                onChange={(e) => setNewJob(p => ({ ...p, job_details: e.target.value }))}
                placeholder="Brief description of the job..."
                rows={3}
                className="w-full border border-slate-300 rounded-sm p-2 text-sm focus:outline-none focus:border-slate-900 resize-y mt-1"
                data-testid="new-job-description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewJob(false)}>Cancel</Button>
            <Button onClick={handleNewJob} className="bg-orange-500 hover:bg-orange-600 text-white gap-1" data-testid="new-job-submit">
              <Plus className="w-4 h-4" /> Create Job
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recall Reason Dialog */}
      <Dialog open={!!recallId} onOpenChange={() => setRecallId(null)}>
        <DialogContent data-testid="recall-dialog">
          <DialogHeader>
            <DialogTitle>Recall Job</DialogTitle>
            <DialogDescription>What is the reason for this recall?</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Recall Reason *</Label>
            <textarea
              value={recallReason}
              onChange={(e) => setRecallReason(e.target.value)}
              placeholder="Describe what the recall is for..."
              rows={4}
              className="w-full border rounded-lg p-3 text-sm mt-1 resize-y focus:outline-none transition-colors"
              style={{ borderColor: "var(--border)", color: "var(--text-primary)" }}
              autoFocus
              data-testid="recall-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecallId(null)}>Cancel</Button>
            <Button
              onClick={async () => {
                if (!recallReason.trim()) { toast.error("Please enter a recall reason"); return; }
                const q = quotes.find(x => x.id === recallId);
                try {
                  await updateQuote(recallId, { pre_recall_status: q?.status || "", recall_reason: recallReason.trim(), status: "Recall" });
                  toast.success("Recall created");
                  setRecallId(null);
                  fetchQuotes();
                } catch { toast.error("Failed to create recall"); }
              }}
              className="btn-primary"
              data-testid="recall-confirm"
            >
              Create Recall
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WO Visit Choice Dialog */}
      <Dialog open={!!woVisitDialog} onOpenChange={() => setWoVisitDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Works Order — Book Visit?</DialogTitle></DialogHeader>
          <p className="text-sm py-2" style={{ color: "var(--text-secondary)" }}>
            {woVisitDialog?.site_address}
          </p>
          <div className="space-y-2 py-2">
            <button
              onClick={() => { const q = woVisitDialog; setWoVisitDialog(null); navigate(`/calendar?book=${q.id}`); }}
              className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm font-semibold transition-colors hover:bg-sky-50"
              style={{ border: "2px solid var(--primary)", color: "var(--primary)" }}
            >
              Book Visit
              <span className="text-xs font-normal" style={{ color: "var(--text-muted)" }}>Schedule a site visit</span>
            </button>
            <button
              onClick={async () => {
                const q = woVisitDialog;
                setWoVisitDialog(null);
                try {
                  await updateQuote(q.id, { status: "Quote Accepted" }, "System");
                  toast.success("Status moved to Quote Accepted — ready to create invoice");
                  fetchQuotes();
                } catch (e) { toast.error("Failed"); }
              }}
              className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm font-semibold transition-colors hover:bg-emerald-50"
              style={{ border: "2px solid var(--success)", color: "#15803D" }}
            >
              No Visit Required
              <span className="text-xs font-normal" style={{ color: "var(--text-muted)" }}>Skip to creating invoice</span>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Accept Quote Selection Dialog */}
      <Dialog open={!!acceptDialog} onOpenChange={() => { setAcceptDialog(null); setAcceptDuplicates([]); }}>
        <DialogContent data-testid="accept-quote-dialog">
          <DialogHeader>
            <DialogTitle>Select Accepted Quote</DialogTitle>
            <DialogDescription>This job has multiple quote options. Select which one the client has accepted.</DialogDescription>
          </DialogHeader>
          {acceptLoading ? (
            <div className="text-center py-8 text-sm" style={{ color: "var(--text-muted)" }}>Loading quote options...</div>
          ) : (
            <div className="space-y-2 py-2 max-h-[400px] overflow-y-auto">
              {acceptDuplicates.map((d) => {
                const isOriginal = !d.is_duplicate;
                return (
                  <button
                    key={d.id}
                    onClick={() => confirmAcceptQuote(d.id)}
                    className="w-full flex items-center gap-4 px-4 py-3 rounded-lg text-left transition-colors hover:bg-emerald-50"
                    style={{ border: "1px solid var(--border)" }}
                    data-testid={`accept-option-${d.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>
                          {d.duplicate_label || (isOriginal ? "Original Quote" : "Option")}
                        </span>
                        <span className="text-xs font-mono font-bold" style={{ color: "var(--primary)" }}>
                          {formatCurrency(d.total_inc_vat)}
                        </span>
                      </div>
                      <p className="text-xs" style={{ color: "var(--text-muted)" }}>
                        {d.item_count || 0} items · {(d.actions || []).filter(a => typeof a === "object" && !a.is_sub).length} services
                      </p>
                    </div>
                    <span className="text-xs font-semibold px-3 py-1.5 rounded-lg flex-shrink-0" style={{ background: "#D1FAE5", color: "#065F46" }}>
                      Accept
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent data-testid="delete-job-dialog">
          <DialogHeader>
            <DialogTitle>Delete Job</DialogTitle>
            <DialogDescription>Are you sure you want to delete this job? This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteId(null)} data-testid="delete-no-btn">No</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="delete-yes-btn">Yes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* See All Dialog */}
      {showAll && <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.5)", zIndex: 998 }} onClick={() => setShowAll(false)} />}
      <Dialog open={showAll} onOpenChange={setShowAll}>
        <DialogContent className="max-h-[85vh] overflow-hidden flex flex-col" data-testid="see-all-dialog" style={{ width: "min(900px, 90vw)", minWidth: "min(800px, 90vw)", maxWidth: "calc(100vw - 32px)", background: "#ffffff", borderRadius: "12px", padding: "28px", zIndex: 999, boxShadow: "0 8px 32px rgba(0,0,0,0.12)" }}>
          <DialogHeader>
            <DialogTitle>All Jobs ({sortedQuotes.length})</DialogTitle>
          </DialogHeader>
          <SeeAllContent quotes={sortedQuotes} onSelect={(q) => { setShowAll(false); navigate(`/quotes/${q.accepted_quote_id || q.id}`); }} onNotesUpdate={handleFieldUpdate} />
        </DialogContent>
      </Dialog>

      {/* Send Quote Dialog */}
      <SendQuoteDialog
        quoteId={sendQuoteId}
        address={quotes.find(q => q.id === sendQuoteId)?.site_address || ""}
        clientName={quotes.find(q => q.id === sendQuoteId)?.client_type || ""}
        clientFullName={(() => { const q = quotes.find(q => q.id === sendQuoteId); return q ? `${q.client_first_name || ""} ${q.client_last_name || ""}`.trim() : ""; })()}
        defaultEmail={quotes.find(q => q.id === sendQuoteId)?.correspondence_email || ""}
        onClose={() => { setSendQuoteId(null); fetchQuotes(); }}
      />
    </div>
  );
}

const HOURS_PER_DAY = 8;

function formatLabourDisplay(hours) {
  if (!hours || hours === 0) return "";
  if (hours >= HOURS_PER_DAY) {
    const days = hours / HOURS_PER_DAY;
    if (Number.isInteger(days)) return `${days} day${days !== 1 ? "s" : ""}`;
    if (days % 0.5 === 0) return `${days} days`;
  }
  return `${hours} hrs`;
}

function MobileJobCard({ quote, index, onNavigate, onBook, onCreateQuote, statusFilter }) {
  const statusColor = STATUS_COLORS[quote.status] || STATUS_COLORS["Not Started"];
  return (
    <div
      className="ui-surface p-3 active:scale-[0.99] transition-transform"
      onClick={onNavigate}
      data-testid={`mobile-job-card-${index}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate" style={{ color: "var(--text-primary)" }}>
            {quote.site_address || "No address"}
          </p>
          <p className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>
            {quote.client_first_name} {quote.client_last_name} {quote.client_type ? `· ${quote.client_type}` : ""}
          </p>
        </div>
        <ChevronRight className="w-4 h-4 flex-shrink-0 mt-1" style={{ color: "var(--text-muted)" }} />
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-semibold ${statusColor}`}>
          {quote.status === "Awaiting Quote" ? "Send Quote!" : (quote.status === "Job Signed" && quote.works_type === "Works Order") ? "Send Invoice!" : (quote.status || "Not Started")}
        </span>
        {quote.is_overdue && quote.status === "Quote Booked" && (
          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded" style={{ background: "#FFF5F2", color: "#C2410C", border: "1px solid #FED7AA" }} title={quote.overdue_reason || "Overdue"} data-testid="quote-overdue-badge">Quote Overdue</span>
        )}
        {quote.works_type === "Works Order" && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-md" style={{ background: "#DBEAFE", color: "#1D4ED8", border: "1px solid #93C5FD" }}>Works Order</span>
        )}
        {quote.status === "Job Signed" && quote.action_completion && (() => {
          const ac = quote.action_completion;
          const status = ac.status || {};
          const mainActs = (quote.actions || []).filter(a => typeof a === "object" && !a.is_sub);
          const incompleteCount = mainActs.filter((_, idx) => !status[idx + 1]).length;
          if (incompleteCount > 0) return (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ background: "var(--danger-light)", color: "var(--danger)" }}>
              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none"><path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              {incompleteCount}
            </span>
          );
          return null;
        })()}
        {quote.start_date && (
          <span className="text-[11px] flex items-center gap-1" style={{ color: "var(--text-secondary)" }}>
            <CalendarIcon className="w-3 h-3" /> {quote.start_date}
          </span>
        )}
        {quote.labour_hours > 0 && (
          <span className="text-[11px] font-mono" style={{ color: "var(--text-secondary)" }}>
            {formatLabourDisplay(quote.labour_hours)}
          </span>
        )}
        {statusFilter === "Quote Sent" && (() => {
          const sentDate = quote.first_sent_date || quote.updated_at;
          const days = sentDate ? Math.floor((Date.now() - new Date(sentDate).getTime()) / (1000*60*60*24)) : 0;
          return (
            <span className="text-[11px] font-mono font-bold px-1.5 py-0.5 rounded" style={{ color: days >= 3 ? "#fff" : "var(--text-secondary)", background: days >= 3 ? "var(--danger)" : "var(--bg)" }}>
              {days}d
            </span>
          );
        })()}
      </div>
      {quote.job_notes && (
        <p className="text-xs mt-1.5 truncate" style={{ color: "var(--text-muted)" }}>{quote.job_notes.length > 60 ? quote.job_notes.slice(0, 60).replace(/\s+/g, " ").trim() + "…" : quote.job_notes}</p>
      )}
    </div>
  );
}

function JobRow({ quote, index, clientTypes, statuses, materialsStatuses, onFieldUpdate, onNavigate, onCreateQuote, onDelete, onSendQuote, onBook, onView, onInvoice, onRecall, onAccept, statusFilter, onWoVisit, isOverdue }) {
  const [hoursVal, setHoursVal] = useState(quote.quote_started ? (quote.labour_hours ?? "") : "");
  const [notesVal, setNotesVal] = useState(quote.job_notes || "");
  const [calOpen, setCalOpen] = useState(false);
  const [notesFocused, setNotesFocused] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);
  const notesDebounceRef = useRef(null);
  const notesSavedTimerRef = useRef(null);
  const notesTextareaRef = useRef(null);

  // Sync from parent when quotes reload (but not while user is typing)
  useEffect(() => { setHoursVal(quote.quote_started ? (quote.labour_hours ?? "") : ""); }, [quote.labour_hours, quote.quote_started]);
  useEffect(() => {
    if (!notesFocused) setNotesVal(quote.job_notes || "");
  }, [quote.job_notes, notesFocused]);
  const commitHours = () => {
    const v = parseFloat(hoursVal) || 0;
    if (v !== quote.labour_hours) onFieldUpdate(quote.id, "labour_hours", v);
  };
  const saveNotes = (val) => {
    if (val !== (quote.job_notes || "")) {
      onFieldUpdate(quote.id, "job_notes", val);
      setNotesSaved(true);
      if (notesSavedTimerRef.current) clearTimeout(notesSavedTimerRef.current);
      notesSavedTimerRef.current = setTimeout(() => setNotesSaved(false), 1800);
    }
  };
  const resizeNotes = () => {
    const el = notesTextareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    // 28px = ~1 line. Rows stay compact when empty; grow only when content needs more.
    el.style.height = Math.max(el.scrollHeight, 28) + "px";
  };
  // Resize on mount + whenever the value changes (typing OR external parent updates while unfocused).
  useEffect(() => { resizeNotes(); }, [notesVal]);
  const handleNotesChange = (e) => {
    const v = e.target.value;
    setNotesVal(v);
    if (notesDebounceRef.current) clearTimeout(notesDebounceRef.current);
    notesDebounceRef.current = setTimeout(() => saveNotes(v), 1000);
  };
  useEffect(() => () => {
    if (notesDebounceRef.current) clearTimeout(notesDebounceRef.current);
    if (notesSavedTimerRef.current) clearTimeout(notesSavedTimerRef.current);
  }, []);

  const parseDate = (str) => {
    if (!str) return undefined;
    try { return parse(str, "dd/MM/yyyy", new Date()); } catch { return undefined; }
  };

  const selectedDate = parseDate(quote.start_date);

  const statusColor = STATUS_COLORS[quote.status] || STATUS_COLORS["Not Started"];
  const materialsColor = MATERIALS_COLORS[quote.materials_status] || MATERIALS_COLORS["Not Started"];

  return (
    <tr
      className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors group align-top"
      data-testid={`job-row-${index}`}
    >
      {/* Address */}
      <td className="px-2 py-1.5">
        <div className="flex items-center gap-2">
          {isOverdue && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded flex-shrink-0" style={{ background: "#FEE2E2", color: "#DC2626" }}>OVERDUE</span>}
          {quote.works_type === "Works Order" && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded flex-shrink-0" style={{ background: "#DBEAFE", color: "#1D4ED8" }}>WO</span>}
          <span className="text-sm font-medium px-1 py-1 truncate flex-1" data-testid={`job-address-${index}`}>
            {quote.site_address || <span className="text-slate-400">No address</span>}
          </span>
          {/* Multi-quote indicator — shows when a job has more than one quote in its family.
              Active Quote is what's displayed in this row; the badge tells the user there are
              superseded variants accessible via View Job Pack. */}
          {(quote.family_size || 1) > 1 && (
            <span
              className="text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0 cursor-help"
              style={{ background: "#FEF3C7", color: "#92400E", border: "1px solid #FDE68A" }}
              title={`This job has ${quote.family_size} quotes. The Active Quote is shown here. Open the job to switch or view others.`}
              data-testid={`family-size-badge-${index}`}
            >
              {quote.family_size} quotes
            </span>
          )}
          {statusFilter === "Quote Sent" && (() => {
            const sentDate = quote.first_sent_date || quote.updated_at;
            const days = sentDate ? Math.floor((Date.now() - new Date(sentDate).getTime()) / (1000*60*60*24)) : 0;
            return (
              <span
                className="text-xs font-mono font-bold flex-shrink-0 px-1.5 py-0.5 rounded"
                style={{
                  color: days >= 3 ? "#fff" : "var(--text-secondary)",
                  background: days >= 3 ? "var(--danger)" : "var(--bg)",
                }}
              >
                {days}d
              </span>
            );
          })()}
          <button
            onClick={onView}
            className="text-[10px] font-semibold text-sky-500 hover:text-sky-600 whitespace-nowrap flex-shrink-0"
            data-testid={`job-view-details-${index}`}
          >
            View Job Pack
          </button>
        </div>
      </td>

      {/* Status */}
      <td className="px-2 py-1.5" style={{ width: "130px", minWidth: "130px" }}>
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className={`inline-block h-8 leading-8 rounded text-xs font-semibold px-2 text-center ${statusColor}`} style={{ flexGrow: 1 }} data-testid={`job-status-${index}`}>
            {quote.status === "Awaiting Quote" ? "Send Quote!" : (quote.status === "Job Signed" && quote.works_type === "Works Order") ? "Send Invoice!" : (quote.status || "Not Started")}
          </span>
          {quote.is_overdue && quote.status === "Quote Booked" && (
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded" style={{ background: "#FFF5F2", color: "#C2410C", border: "1px solid #FED7AA" }} title={quote.overdue_reason || "Overdue"} data-testid="quote-overdue-badge">Quote Overdue</span>
          )}
          {quote.status === "Job Signed" && quote.action_completion && (() => {
            const ac = quote.action_completion;
            const status = ac.status || {};
            const mainActs = (quote.actions || []).filter(a => typeof a === "object" && !a.is_sub);
            const incompleteCount = mainActs.filter((_, idx) => !status[idx + 1]).length;
            if (incompleteCount > 0) {
              return (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ background: "var(--danger-light)", color: "var(--danger)" }} title={`${incompleteCount} action${incompleteCount > 1 ? "s" : ""} not completed`} data-testid={`incomplete-warning-${index}`}>
                  <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none"><path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  {incompleteCount}
                </span>
              );
            }
            return null;
          })()}
        </div>
      </td>

      {/* Start Date */}
      <td className="px-2 py-1.5" style={{ width: "100px", minWidth: "100px" }}>
        <div className="flex items-center gap-1.5 h-8 px-2 text-xs w-full">
          <CalendarIcon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
          <span className={quote.start_date ? "text-slate-700" : "text-slate-400"}>
            {quote.start_date || "dd/mm/yyyy"}
          </span>
        </div>
      </td>

      {/* Book */}
      <td className="px-2 py-1.5" style={{ width: "120px", minWidth: "120px" }}>
        {quote.status === "Not Started" ? (
          quote.works_type === "Works Order" ? (
            <button
              onClick={() => onWoVisit(quote)}
              className="inline-flex items-center justify-center w-full px-2.5 py-1 text-[11px] font-semibold rounded bg-sky-500 text-white hover:bg-sky-600 transition-colors"
              data-testid={`job-book-${index}`}
            >
              Book Visit
            </button>
          ) : (
            <button
              onClick={onBook}
              className="inline-flex items-center justify-center w-full px-2.5 py-1 text-[11px] font-semibold rounded bg-sky-500 text-white hover:bg-sky-600 transition-colors"
              data-testid={`job-book-${index}`}
            >
              Book Quote
            </button>
          )
        ) : quote.status === "Quote Accepted" ? (
          <button
            onClick={onBook}
            className="inline-flex items-center justify-center w-full px-2.5 py-1 text-[11px] font-semibold rounded bg-sky-500 text-white hover:bg-sky-600 transition-colors"
            data-testid={`job-book-${index}`}
          >
            Book Execution
          </button>
        ) : quote.status === "Recall" && quote.booking_type !== "recall" ? (
          <button
            onClick={onBook}
            className="inline-flex items-center justify-center w-full px-2.5 py-1 text-[11px] font-semibold rounded bg-orange-500 text-white hover:bg-orange-600 transition-colors"
            data-testid={`job-book-${index}`}
          >
            Book Recall
          </button>
        ) : (quote.status === "Job Signed" || quote.status === "Invoiced") ? (
          <button
            onClick={onRecall}
            className="inline-flex items-center justify-center w-full px-2.5 py-1 text-[11px] font-semibold rounded border border-orange-300 text-orange-600 hover:bg-orange-50 transition-colors"
            data-testid={`job-recall-${index}`}
          >
            Recall
          </button>
        ) : quote.status === "Awaiting Quote" ? (
          <button
            onClick={onBook}
            className="inline-flex items-center justify-center w-full px-2.5 py-1 text-[11px] font-semibold rounded border border-blue-300 text-blue-600 hover:bg-blue-50 transition-colors"
            data-testid={`job-book-visit-${index}`}
          >
            Book Visit
          </button>
        ) : quote.status === "Overdue" ? (
          <button
            onClick={onBook}
            className="inline-flex items-center justify-center w-full px-2.5 py-1 text-[11px] font-semibold rounded bg-red-500 text-white hover:bg-red-600 transition-colors"
            data-testid={`job-book-${index}`}
          >
            Rebook
          </button>
        ) : (
          <span className="inline-flex items-center justify-center w-full px-2.5 py-1 text-[11px] font-semibold rounded bg-slate-200 text-slate-400 cursor-default">
            Booked
          </span>
        )}
      </td>

      {/* Materials */}
      <td className="px-2 py-1.5" style={{ width: "110px", minWidth: "110px" }}>
        {["Quote Accepted", "Job Booked", "Job Signed", "Invoiced", "Recall"].includes(quote.status) ? (
          <Select value={quote.materials_status || "Not Started"} onValueChange={(v) => onFieldUpdate(quote.id, "materials_status", v)}>
            <SelectTrigger
              className={`h-8 border-0 rounded text-xs font-medium shadow-none px-2 ${materialsColor}`}
              data-testid={`job-materials-${index}`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {materialsStatuses.map(m => (
                <SelectItem key={m} value={m}>
                  <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${MATERIALS_COLORS[m] || ""}`}>{m}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <span className="inline-block h-8 leading-8 rounded text-xs font-medium px-2 bg-slate-100 text-slate-400" data-testid={`job-materials-${index}`}>
            Not Started
          </span>
        )}
      </td>

      {/* Hours */}
      <td className="px-2 py-1.5">
        {quote.quote_started ? (
          <div className="text-sm font-mono px-1 py-1 text-left text-slate-700">
            {formatLabourDisplay(quote.labour_hours)}
          </div>
        ) : (
          <input
            type="number"
            step="0.5"
            min="0"
            value={hoursVal}
            onChange={(e) => setHoursVal(e.target.value)}
            onBlur={commitHours}
            onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
            className="w-full bg-transparent border-0 outline-none text-sm font-mono px-1 py-1 rounded hover:bg-slate-100 focus:bg-white focus:ring-1 focus:ring-slate-300 text-left"
            data-testid={`job-hours-${index}`}
          />
        )}
      </td>

      {/* Notes */}
      <td className="px-2 py-1.5" style={{ width: "200px", maxWidth: "200px" }}>
        <div className="relative">
          <textarea
            ref={notesTextareaRef}
            value={notesVal}
            onFocus={() => setNotesFocused(true)}
            onBlur={() => {
              setNotesFocused(false);
              if (notesDebounceRef.current) { clearTimeout(notesDebounceRef.current); notesDebounceRef.current = null; }
              saveNotes(notesVal);
            }}
            onChange={(e) => { handleNotesChange(e); resizeNotes(); }}
            onInput={resizeNotes}
            placeholder="Notes"
            rows={1}
            className="w-full bg-transparent border-0 outline-none text-sm px-1 py-1 rounded text-slate-600 resize-none overflow-hidden focus:bg-white focus:ring-1 focus:ring-slate-300 hover:bg-slate-100"
            style={{ minHeight: "28px", wordBreak: "break-word" }}
            data-testid={`job-notes-${index}`}
          />
          {notesSaved && (
            <span className="absolute -top-2 right-1 text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ color: "#16a34a", background: "rgba(22,163,74,0.08)" }} data-testid={`job-notes-saved-${index}`}>Saved</span>
          )}
        </div>
      </td>

      {/* Quote */}
      <td className="px-2 py-1.5" style={{ width: "140px", minWidth: "140px" }}>
        {quote.quote_started ? (
          <div className="flex items-center gap-1.5">
            <button
              onClick={onNavigate}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded bg-orange-500 text-white hover:bg-orange-600 transition-colors"
              data-testid={`job-view-quote-${index}`}
            >
              View Quote
            </button>
            {quote.status === "Quote Sent" || quote.status === "Quote Accepted" || quote.status === "Job Booked" || quote.status === "Job Signed" || quote.status === "Invoiced" ? (
              <span
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded bg-slate-200 text-slate-400 cursor-default"
              >
                <Send className="w-3 h-3" /> Sent
              </span>
            ) : (
              <button
                onClick={onSendQuote}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded bg-sky-400 text-white hover:bg-sky-500 transition-colors"
                data-testid={`job-send-quote-${index}`}
              >
                <Send className="w-3 h-3" /> Send
              </button>
            )}
            {quote.status === "Quote Sent" && (
              <button
                onClick={onAccept}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded bg-emerald-500 text-white hover:bg-emerald-600 transition-colors"
                data-testid={`job-accept-quote-${index}`}
              >
                Accept
              </button>
            )}
            {quote.status === "Job Signed" && (
              <button
                onClick={() => onInvoice(quote.id)}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded bg-emerald-500 text-white hover:bg-emerald-600 transition-colors"
                data-testid={`job-invoice-${index}`}
              >
                Invoice
              </button>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-xs text-red-500 font-medium">Not Started</span>
            <button
              onClick={onCreateQuote}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded border border-slate-300 text-slate-600 hover:bg-slate-100 transition-colors"
              data-testid={`job-create-quote-${index}`}
            >
              {quote.works_type === "Works Order" ? "Create Invoice" : "Create Quote"}
            </button>
          </div>
        )}
      </td>

      {/* Delete */}
      <td className="px-2 py-1.5">
        <button
          onClick={onDelete}
          className="w-7 h-7 flex items-center justify-center rounded text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"
          data-testid={`job-delete-${index}`}
          title="Delete job"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </td>
    </tr>
  );
}


function SortHeader({ col, label, sortCol, sortDir, onSort, className }) {
  const active = sortCol === col;
  return (
    <th
      className={`text-left px-3 py-2.5 text-xs font-semibold uppercase tracking-wider cursor-pointer select-none transition-colors hover:bg-slate-100 ${
        active ? "text-slate-900" : "text-slate-500"
      } ${className || ""}`}
      onClick={() => onSort(col)}
      data-testid={`sort-${col}`}
    >
      <span className="flex items-center gap-1">
        {label}
        {active && (
          <span className="text-orange-500">{sortDir === "asc" ? "\u25B2" : "\u25BC"}</span>
        )}
        {!active && <span className="text-slate-300 text-[9px] font-normal normal-case tracking-normal">recent</span>}
      </span>
    </th>
  );
}


function SendQuoteDialog({ quoteId, address, clientName, clientFullName, defaultEmail, onClose }) {
  const [sending, setSending] = useState(false);
  if (!quoteId) return null;
  const greeting = clientFullName || clientName || "";
  return (
    <Dialog open={!!quoteId} onOpenChange={() => onClose()}>
      <DialogContent data-testid="send-quote-dialog">
        <form onSubmit={async (e) => { e.preventDefault(); const fd = new FormData(e.target); const to = fd.get("to_email"); if (!to) { toast.error("Enter email"); return; } setSending(true); try { await sendQuoteEmail(quoteId, { to_email: to, cc_email: fd.get("cc_email")||"", subject: fd.get("subject")||"", message: fd.get("message")||"" }); toast.success("Quote sent"); onClose(); } catch { toast.error("Failed"); } finally { setSending(false); } }}>
          <DialogHeader><DialogTitle>Send Quote</DialogTitle><DialogDescription>Send this quote as a PDF via email</DialogDescription></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="text-xs uppercase text-slate-500">To *</Label><input name="to_email" type="email" defaultValue={defaultEmail} placeholder="recipient@example.com" className="w-full h-9 rounded-lg px-3 text-sm mt-1" style={{ border: "1px solid var(--border)" }} data-testid="send-to-email" /></div>
            <div><Label className="text-xs uppercase text-slate-500">CC</Label><input name="cc_email" defaultValue="" placeholder="cc@example.com" className="w-full h-9 rounded-lg px-3 text-sm mt-1" style={{ border: "1px solid var(--border)" }} /></div>
            <div><Label className="text-xs uppercase text-slate-500">Subject</Label><input name="subject" defaultValue={`${address} Quotation is available!`} className="w-full h-9 rounded-lg px-3 text-sm mt-1" style={{ border: "1px solid var(--border)" }} /></div>
            <div><Label className="text-xs uppercase text-slate-500">Message</Label><textarea name="message" defaultValue={`Hi ${greeting},\n\nPlease see your quotation attached.\n\nIf you have any questions feel free to give us a call.\n\nMany Thanks`} rows={4} className="w-full rounded-lg p-3 text-sm mt-1 resize-y" style={{ border: "1px solid var(--border)" }} /></div>
          </div>
          <DialogFooter className="gap-2">
            <button type="button" onClick={() => window.open(getQuotePdfUrl(quoteId), "_blank")} className="inline-flex items-center gap-1 px-3 py-2 text-sm rounded border" style={{ borderColor: "var(--border)" }}><Download className="w-4 h-4" /> PDF</button>
            <Button type="submit" disabled={sending} className="bg-sky-500 hover:bg-sky-600 text-white gap-1"><Send className="w-4 h-4" /> {sending ? "Sending..." : "Send"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AllJobsNotesCell({ quote, index, onNotesUpdate }) {
  const [val, setVal] = useState(quote.job_notes || "");
  const [focused, setFocused] = useState(false);
  const [saved, setSaved] = useState(false);
  const debounceRef = useRef(null);
  const savedTimerRef = useRef(null);
  const taRef = useRef(null);

  // Sync from parent when quotes reload (but not while user is typing)
  useEffect(() => { if (!focused) setVal(quote.job_notes || ""); }, [quote.job_notes, focused]);

  const resize = () => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.max(el.scrollHeight, 48) + "px";
  };
  useEffect(() => { resize(); }, [val]);

  const save = (v) => {
    if (v !== (quote.job_notes || "")) {
      onNotesUpdate && onNotesUpdate(quote.id, "job_notes", v);
      setSaved(true);
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => setSaved(false), 1800);
    }
  };
  const onChange = (e) => {
    const v = e.target.value;
    setVal(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => save(v), 1000);
  };
  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
  }, []);

  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <textarea
        ref={taRef}
        value={val}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
          save(val);
        }}
        onChange={(e) => { onChange(e); resize(); }}
        onInput={resize}
        placeholder="Add notes…"
        rows={1}
        className="w-full bg-transparent border-0 outline-none text-sm px-1 py-1 rounded text-slate-600 resize-none overflow-hidden focus:bg-white focus:ring-1 focus:ring-slate-300 hover:bg-slate-100"
        style={{ minHeight: "28px" }}
        data-testid={`all-jobs-notes-textarea-${index}`}
      />
      {saved && (
        <span className="absolute -top-2 right-1 text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ color: "#16a34a", background: "rgba(22,163,74,0.08)" }}>Saved</span>
      )}
    </div>
  );
}

function SeeAllContent({ quotes, onSelect, onNotesUpdate }) {
  const [search, setSearch] = useState("");
  const [statusF, setStatusF] = useState("");
  const [clientF, setClientF] = useState("");
  const [dateF, setDateF] = useState("");

  const filtered = useMemo(() => {
    let result = quotes;
    if (search) {
      result = result.filter(q => {
        const combined = [q.site_address, q.client_type, q.client_first_name, q.client_last_name, q.quote_number, q.status, q.job_notes, q.booked_team_member_name].filter(Boolean).join(" ");
        return flexMatch(combined, search);
      });
    }
    if (statusF) result = result.filter(q => q.status === statusF);
    if (clientF) result = result.filter(q => q.client_type === clientF);
    if (dateF) {
      const now = new Date();
      const startOfWeek = new Date(now); startOfWeek.setDate(now.getDate() - now.getDay() + 1); startOfWeek.setHours(0,0,0,0);
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
      result = result.filter(q => {
        if (!q.start_date) return false;
        const parts = q.start_date.split("/");
        if (parts.length < 3) return false;
        const d = new Date(2000 + parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
        if (dateF === "week") return d >= startOfWeek;
        if (dateF === "month") return d >= startOfMonth;
        if (dateF === "last_month") return d >= startOfLastMonth && d <= endOfLastMonth;
        return true;
      });
    }
    return result;
  }, [quotes, search, statusF, clientF, dateF]);

  const clients = [...new Set(quotes.map(q => q.client_type).filter(Boolean))].sort();

  return (
    <>
      {/* Search bar */}
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "var(--text-muted)" }} />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by address, client, status, quote number, notes..."
          className="w-full h-10 pl-10 pr-10 rounded-lg text-sm"
          style={{ border: "1px solid var(--border)", background: "var(--surface)" }}
        />
        {search && <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }}><X className="w-4 h-4" /></button>}
      </div>
      {/* Filter row */}
      <div className="flex gap-2 mb-3">
        <select value={statusF} onChange={(e) => setStatusF(e.target.value)} className="h-8 rounded-md text-xs px-2" style={{ border: "1px solid var(--border)", background: "var(--surface)" }}>
          <option value="">All Statuses</option>
          {["Not Started","Quote Booked","Awaiting Quote","Quote Sent","Quote Accepted","Job Booked","Job Signed","Invoiced","Recall","Recall Booked","Cancelled"].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={clientF} onChange={(e) => setClientF(e.target.value)} className="h-8 rounded-md text-xs px-2" style={{ border: "1px solid var(--border)", background: "var(--surface)" }}>
          <option value="">All Clients</option>
          {clients.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={dateF} onChange={(e) => setDateF(e.target.value)} className="h-8 rounded-md text-xs px-2" style={{ border: "1px solid var(--border)", background: "var(--surface)" }}>
          <option value="">All Time</option>
          <option value="week">This Week</option>
          <option value="month">This Month</option>
          <option value="last_month">Last Month</option>
        </select>
        {(statusF || clientF || dateF) && <button onClick={() => { setStatusF(""); setClientF(""); setDateF(""); }} className="text-xs font-semibold" style={{ color: "var(--primary)" }}>Clear</button>}
        <span className="ml-auto text-xs" style={{ color: "var(--text-muted)" }}>{filtered.length} results</span>
      </div>
      {/* Table */}
      <div className="overflow-y-auto flex-1 -mx-6 px-6">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center py-16">
            <Search className="w-10 h-10 mb-4" style={{ color: "#9CA3AF" }} />
            <p style={{ fontSize: "15px", fontWeight: 600, color: "#374151" }}>No jobs match your search</p>
            <p style={{ fontSize: "13px", color: "#6B7280", marginTop: "8px" }}>Try a different address or quote number</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white z-10">
              <tr className="border-b border-slate-200">
                <th className="text-left py-2 text-[11px] font-bold uppercase text-slate-500 pr-4">Address</th>
                <th className="text-left py-2 text-[11px] font-bold uppercase text-slate-500 pr-4">Client</th>
                <th className="text-left py-2 text-[11px] font-bold uppercase text-slate-500 pr-4">Status</th>
                <th className="text-left py-2 text-[11px] font-bold uppercase text-slate-500 pr-4">Contractor</th>
                <th className="text-left py-2 text-[11px] font-bold uppercase text-slate-500 pr-4">Date</th>
                <th className="text-left py-2 text-[11px] font-bold uppercase text-slate-500">Notes</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((q, i) => {
                return (
                  <tr key={q.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors align-top" data-testid={`all-job-row-${i}`}>
                    <td className="py-3 pr-4 text-sm font-medium truncate max-w-[250px] cursor-pointer" onClick={() => onSelect(q)}>{q.site_address || "No address"}</td>
                    <td className="py-3 pr-4 text-xs cursor-pointer" onClick={() => onSelect(q)}>{q.client_type}</td>
                    <td className="py-3 pr-4 cursor-pointer" onClick={() => onSelect(q)}><span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold status-${(q.status || "not-started").toLowerCase().replace(/\s+/g, "-")}`}>{q.status || "Not Started"}</span></td>
                    <td className="py-3 pr-4 text-xs cursor-pointer" style={{ color: "var(--text-muted)" }} onClick={() => onSelect(q)}>{q.booked_team_member_name || "—"}</td>
                    <td className="py-3 pr-4 text-xs cursor-pointer" style={{ color: "var(--text-muted)" }} onClick={() => onSelect(q)}>{q.start_date || "—"}</td>
                    <td className="py-3 text-xs" style={{ color: "var(--text-muted)", width: "260px", maxWidth: "260px" }}>
                      <AllJobsNotesCell quote={q} index={i} onNotesUpdate={onNotesUpdate} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

